import React, { useState, useEffect } from 'react';
import { useToast } from '../../context/ToastContext';
import { Card, Badge, SectionHeader, Spinner } from '../../components/shared/UI';
import API from '../../utils/apiClient';

const TODAY = new Date().toISOString().split('T')[0];
const DAY_COLORS = {
  past:     'border-gray-200 bg-gray-50',
  today:    'border-brand-400 bg-brand-50 shadow-sm',
  upcoming: 'border-gray-200 bg-white hover:border-brand-300',
};

function classifyDate(dateStr) {
  if (dateStr < TODAY) return 'past';
  if (dateStr === TODAY) return 'today';
  return 'upcoming';
}

export default function TeacherTimetablePage() {
  const { addToast } = useToast();
  const [timetable, setTimetable] = useState([]);
  const [loading, setLoading]     = useState(true);
  const [weekOffset, setWeek]     = useState(0);

  useEffect(() => {
    API.get('/timetable/teacher')
      .then(({ data }) => setTimetable(data.timetable || []))
      .catch(() => addToast('Failed to load timetable', 'error'))
      .finally(() => setLoading(false));
  }, []);

  // Calculate week window
  const weekStart = new Date();
  weekStart.setDate(weekStart.getDate() - weekStart.getDay() + 1 + weekOffset * 7); // Monday
  const weekEnd = new Date(weekStart);
  weekEnd.setDate(weekEnd.getDate() + 6);

  const weekDates = Array.from({ length: 7 }, (_, i) => {
    const d = new Date(weekStart);
    d.setDate(d.getDate() + i);
    return d.toISOString().split('T')[0];
  });

  const inWeek = timetable.filter(e => {
    const d = e.date?.split('T')[0];
    return d >= weekDates[0] && d <= weekDates[6];
  });

  const formatDate = d => new Date(d).toLocaleDateString('en-GB', { weekday:'short', day:'numeric', month:'short' });

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 py-8">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <SectionHeader title="My Timetable" subtitle="Your weekly class schedule" />
        <div className="flex items-center gap-2">
          <button onClick={() => setWeek(p => p - 1)}
            className="px-3 py-1.5 text-sm border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors">← Prev</button>
          <button onClick={() => setWeek(0)}
            className="px-3 py-1.5 text-sm bg-brand-500 text-white rounded-lg font-semibold hover:bg-brand-600 transition-colors">Today</button>
          <button onClick={() => setWeek(p => p + 1)}
            className="px-3 py-1.5 text-sm border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors">Next →</button>
        </div>
      </div>

      <p className="text-xs text-gray-500 text-center mb-4 font-medium">
        {formatDate(weekDates[0])} — {formatDate(weekDates[6])}
        {weekOffset === 0 && <span className="ml-2 px-2 py-0.5 bg-brand-50 text-brand-500 rounded-full text-xs font-bold">This Week</span>}
      </p>

      {loading ? (
        <div className="flex justify-center py-16"><Spinner size="lg" className="text-brand-500" /></div>
      ) : (
        <div className="space-y-3">
          {weekDates.map(date => {
            const dayEntries = inWeek.filter(e => e.date?.split('T')[0] === date);
            const status = classifyDate(date);
            const dayName = new Date(date).toLocaleDateString('en-GB', { weekday:'long', day:'numeric', month:'short' });

            return (
              <div key={date} className={`rounded-xl border-2 transition-all ${DAY_COLORS[status]}`}>
                <div className={`flex items-center gap-3 px-4 py-3 border-b ${status === 'today' ? 'border-brand-300' : 'border-gray-100'}`}>
                  <span className={`text-xs font-bold ${status === 'today' ? 'text-brand-500' : 'text-gray-400'}`}>
                    {status === 'today' && '● '}
                    {dayName}
                  </span>
                  {status === 'today' && <span className="text-xs bg-brand-500 text-white px-2 py-0.5 rounded-full font-bold">TODAY</span>}
                  {dayEntries.length > 0 && (
                    <span className="ml-auto text-xs text-gray-400">{dayEntries.length} class{dayEntries.length > 1 ? 'es' : ''}</span>
                  )}
                </div>

                {dayEntries.length === 0 ? (
                  <p className="px-4 py-3 text-xs text-gray-400 italic">No classes scheduled</p>
                ) : (
                  <div className="divide-y divide-gray-100">
                    {dayEntries.map(e => (
                      <div key={e.id} className="px-4 py-3 flex items-start justify-between gap-4">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <Badge variant="gold" size="xs">{e.level}</Badge>
                            <span className="text-xs text-gray-500">{e.cohort_name}</span>
                            <span className="text-xs text-gray-400">{e.start_time}–{e.end_time}</span>
                          </div>
                          <p className="text-sm font-semibold text-de-black">{e.topic}</p>
                          {e.description && <p className="text-xs text-gray-500 mt-0.5">{e.description}</p>}
                        </div>
                        {(e.meet_link || e.cohort_meet_link) && (
                          <a href={e.meet_link || e.cohort_meet_link}
                            target="_blank" rel="noopener noreferrer"
                            className="flex-shrink-0 flex items-center gap-1.5 px-3 py-1.5 bg-brand-500 text-white rounded-lg text-xs font-bold hover:bg-brand-600 transition-colors">
                            📹 Join
                          </a>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}

          {inWeek.length === 0 && !loading && (
            <div className="text-center py-8 text-gray-400">
              <p className="text-2xl mb-2">📅</p>
              <p className="text-sm">No classes scheduled for this week.</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
